import { useBrandMarker } from '@/utils/home-intl/brand-glossary';

import styles from './videos.module.css';

export type VideoVariant = 'sem' | 'com';

// Desenho fixo do gráfico (o mesmo HTML do protótipo): é o que aparece sem JS, antes do controlador chegar e com
// movimento reduzido. viewBox 160 × 30; 0 ms embaixo (y 28), 260 ms em cima (y 4).
const CHART = {
  sem: {
    area: 'M0 15.2L4.9 6L9.8 14.3L14.6 9.6L19.5 12.9L24.4 8L34.1 6.9L39 12.8L43.9 7.9L48.8 13L53.6 7.5L58.5 12.6L63.4 9.4L73.1 7.1L78 13L82.9 10.4L87.8 13L92.6 8.3L97.5 12.2L102.4 6.6L107.3 14.8L117 12.2L121.9 7.2L126.8 13.1L131.6 7.8L136.5 12.3L141.4 6L146.3 14L151.1 9L156 7.3V30H0Z',
    line: 'M0 15.2L4.9 6L9.8 14.3L14.6 9.6L19.5 12.9L24.4 8M34.1 6.9L39 12.8L43.9 7.9L48.8 13L53.6 7.5L58.5 12.6L63.4 9.4M73.1 7.1L78 13L82.9 10.4L87.8 13L92.6 8.3L97.5 12.2L102.4 6.6L107.3 14.8M117 12.2L121.9 7.2L126.8 13.1L131.6 7.8L136.5 12.3L141.4 6L146.3 14L151.1 9L156 7.3',
    drop: 'M29.3 26V29M68.3 26V29M112.1 26V29',
    tipY: 7.3,
    ping: '238',
    loss: 22,
  },
  com: {
    area: 'M0 28L4.9 28L9.8 28L14.6 28L19.5 28L24.4 28L29.3 28L34.1 28L39 28L43.9 28L48.8 28L53.6 28L58.5 28L63.4 28L68.3 28L73.1 28L78 28L82.9 28L87.8 28L92.6 28L97.5 28L102.4 28L107.3 28L112.1 28L117 28L121.9 28L126.8 28L131.6 28L136.5 28L141.4 28L146.3 28L151.1 28L156 28V30H0Z',
    line: 'M0 28L4.9 28L9.8 28L14.6 28L19.5 28L24.4 28L29.3 28L34.1 28L39 28L43.9 28L48.8 28L53.6 28L58.5 28L63.4 28L68.3 28L73.1 28L78 28L82.9 28L87.8 28L92.6 28L97.5 28L102.4 28L107.3 28L112.1 28L117 28L121.9 28L126.8 28L131.6 28L136.5 28L141.4 28L146.3 28L151.1 28L156 28',
    drop: '',
    tipY: 28,
    ping: '4',
    loss: 0,
  },
} as const;

/** id do resumo (sr-only) de um holograma: o <video> da mesma figura aponta para ele com aria-describedby. */
export const hologramSummaryId = (variant: VideoVariant) => `vcs-sum-${variant}`;

/** Valores fixos do holograma (usados também pelo teste). */
export const HOLOGRAM_FIXED = { sem: { ping: CHART.sem.ping, loss: CHART.sem.loss }, com: { ping: CHART.com.ping, loss: CHART.com.loss } } as const;

interface HologramProps {
  variant: VideoVariant;
  mode: string;
  state: string;
  summary: string;
  unit: string;
  lossLabel: string;
  lossValue: string;
  /** Direção do texto do idioma da página (a grade dos vídeos é sempre LTR: o vidro fica no mesmo canto em todos os idiomas). */
  dir: 'ltr' | 'rtl';
  /** Decorative preview keeps the same graph/engine hooks without exposing unqualified simulated measurements. */
  hideReadouts?: boolean;
}

/**
 * Holograma "Sem NoPing" / "Com NoPing" = o <figcaption> do vídeo: painel de vidro no canto de cima de fora, com gráfico
 * de ping, ms e perda de pacotes. Os números são ilustrativos e mudam só na tela (aria-hidden, animados pelo
 * videos-controller.ts); o leitor de tela ouve o modo + o resumo fixo (sr-only).
 */
export function Hologram({ variant, mode, state, summary, unit, lossLabel, lossValue, dir, hideReadouts = false }: HologramProps) {
  const chart = CHART[variant];
  const clipId = `vcs-clip-${variant}`;
  const mark = useBrandMarker();

  return (
    <figcaption className={`${styles.holo} ${variant === 'sem' ? styles.holoSem : styles.holoCom}`} data-vcs-holo={variant}>
      <div className={styles.holoIn} dir={dir}>
        <div className={styles.holoHead}>
          <span className={styles.holoMode}>{mark(mode)}</span>
          {/* resumo logo depois do modo: no texto da página e no leitor de tela vira uma frase só ("Sem NoPing: conexão
              instável…"; o resumo da tradução começa com ': '). sr-only é absoluto: não entra no flex nem muda o desenho */}
          {!hideReadouts && (
            <span id={hologramSummaryId(variant)} className='sr-only normal-case tracking-normal'>
              {summary}
            </span>
          )}
          {state ? (
            <span className={styles.holoState} aria-hidden='true' hidden={hideReadouts} style={hideReadouts ? { display: 'none' } : undefined}>
              <i className={styles.holoDot} />
              {state}
            </span>
          ) : null}
        </div>
        <svg className={styles.holoChart} viewBox='0 0 160 30' aria-hidden='true' focusable='false'>
          <defs>
            <clipPath id={clipId}>
              <rect width='156.5' height='30' />
            </clipPath>
          </defs>
          <path className={styles.chartGrid} d='M0 7.5H160M0 15H160M0 22.5H160' />
          <g clipPath={`url(#${clipId})`}>
            <g data-part='plot'>
              <path className={styles.chartArea} data-part='area' d={chart.area} />
              <path className={styles.chartLine} data-part='line' d={chart.line} />
              <path className={styles.chartDrop} data-part='drop' d={chart.drop} />
            </g>
          </g>
          <circle className={styles.chartTip} data-part='tip' r='1.7' cx='156' cy={chart.tipY} />
        </svg>
        <div className={styles.holoPing} aria-hidden='true' hidden={hideReadouts} style={hideReadouts ? { display: 'none' } : undefined}>
          <strong data-v='ping'>{chart.ping}</strong>
          <span>{unit}</span>
        </div>
        <div className={styles.holoLoss} aria-hidden='true' hidden={hideReadouts} style={hideReadouts ? { display: 'none' } : undefined}>
          <span>{lossLabel}</span>
          <strong data-v='loss'>{lossValue}</strong>
        </div>
      </div>
    </figcaption>
  );
}
